import { useCallback, useEffect, useState } from "react";
import { Search, Camera } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { ccApi, errMsg, fmtTs } from "./ccApi";
import { ActionBtn, AuditRow, Empty, Pill, Section } from "./ccUi";
import ReasonDialog from "./ReasonDialog";
import { captureAndUpload } from "./evidence";

const RESTRICTIONS = [["posting", "Posting"], ["messaging", "Messaging"], ["live", "Going Live"]];

export default function MemberTab({ target, setTarget, me }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [rec, setRec] = useState(null);
  const [err, setErr] = useState("");
  const [dlg, setDlg] = useState(null);

  const load = useCallback(async (id) => {
    setErr(""); setRec(null);
    try { setRec(await ccApi.member(id)); } catch (e) { setErr(errMsg(e, "Member not found")); }
  }, []);

  useEffect(() => { if (target?.type === "user" && (target.id || target.handle)) load(target.id || target.handle); }, [target, load]);

  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return undefined; }
    const t = setTimeout(() => ccApi.searchMembers(q).then((d) => setHits(d.members)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const run = (spec, fn) => setDlg({ ...spec, fn });
  const submit = async ({ reason, confirm, hours }) => {
    try {
      const out = await dlg.fn({ reason, confirm, hours: hours === undefined ? undefined : hours });
      toast.success(`${dlg.title} — logged${out?.case_id ? ` · case ${out.case_id.slice(0, 8)}` : ""}`);
      setDlg(null);
      load(rec.member.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const m = rec?.member;
  const uid = m?.id;
  useEffect(() => {
    if (target?.evidence && m && me?.can?.evidence && m.tier !== "owner") {
      setDlg({ title: "Capture evidence (profile screenshot)", cta: "Capture", fn: ({ reason }) => captureAndUpload({ target: { type: "user", id: m.id, accountId: m.id }, reasonCode: reason.slice(0, 60), selectorHint: "main" }) });
    } else if (target?.evidence && m && me && !me?.can?.evidence) {
      toast.error("Evidence capture requires Safety Admin or owner access.");
    }
  }, [target?.evidence, m?.id, me?.can?.evidence]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = (action, body) => ccApi.memberAction(uid, action, body);
  const suspended = m && (m.banned_until && new Date(m.banned_until) > new Date());
  const canEvidence = me?.can?.evidence;

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input data-testid="cc-member-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search @handle, name, email or ID"
          className="pl-8 bg-black/40 border-white/10 text-white" />
        {hits.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-zinc-950 border border-white/10 rounded-md max-h-56 overflow-auto" data-testid="cc-member-hits">
            {hits.map((h) => (
              <button key={h.id} type="button" data-testid={`cc-member-hit-${h.handle}`} onClick={() => { setQ(""); setHits([]); setTarget({ type: "user", id: h.id, handle: h.handle }); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center gap-2">
                <img src={h.avatar_url || "/favicon.ico"} alt="" className="w-6 h-6 rounded-full object-cover bg-zinc-800" />
                <span className="text-white">{h.display_name}</span>{h.handle && <span className="text-zinc-500 text-xs font-mono">@{h.handle}</span>}
                {h.is_owner && <Pill tone="amber">owner</Pill>}{h.role === "admin" && !h.is_owner && <Pill tone="blue">{h.admin_scope || "admin"}</Pill>}
                {h.banned && <Pill tone="red">banned</Pill>}
              </button>
            ))}
          </div>
        )}
      </div>
      {err && <div data-testid="cc-member-error" className="text-xs text-rose-300">{err}</div>}
      {!m && !err && <Empty>Search for a member, or open the Control Center from a profile.</Empty>}
      {m && (
        <>
          <div data-testid="cc-member-card" className="flex items-start gap-3">
            <img src={m.avatar_url || "/favicon.ico"} alt="" className="w-14 h-14 rounded-full object-cover bg-zinc-800 border border-white/10" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-white font-semibold">{m.display_name}</span>{m.handle && <span className="text-zinc-400 font-mono text-xs" data-testid="cc-member-internal-handle">@{m.handle}</span>}
                <Pill tone={m.tier === "owner" ? "amber" : m.tier === "member" ? "zinc" : "blue"} testid="cc-member-tier">{m.tier}</Pill>
                {m.banned && <Pill tone="red" testid="cc-member-banned">banned</Pill>}
                {suspended && <Pill tone="amber" testid="cc-member-suspended">suspended until {fmtTs(m.banned_until)}</Pill>}
                {rec.live_now && <Pill tone="red">LIVE now</Pill>}
              </div>
              <div className="text-xs text-zinc-400 truncate">{m.email ? m.email : <span className="italic">internal handle & email visible to owner / Safety Admins only</span>}</div>
              <div className="text-[11px] text-zinc-500 font-mono truncate">id {m.id}</div>
              <div className="text-xs text-zinc-400 mt-1">Joined {fmtTs(m.created_at)} · {rec.counts.posts} posts · {rec.counts.streams} lives · {rec.counts.reports_against} reports against · coins {m.coins ?? 0}</div>
              {m.sessions_revoked_at && <div className="text-[11px] text-amber-300">Sessions revoked {fmtTs(m.sessions_revoked_at)}</div>}
            </div>
          </div>
          {m.tier === "owner" ? (
            <div data-testid="cc-owner-locked" className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-md p-3">The owner account is protected — no Control Center action can target it.</div>
          ) : (
            <Section title="Quick actions" testid="cc-quick-actions">
              <div className="flex flex-wrap gap-2">
                {m.banned || suspended ? (
                  <ActionBtn tone="good" testid="cc-action-unsuspend" onClick={() => run({ title: "Lift suspension / unban", cta: "Lift" }, ({ reason }) => act(m.banned ? "unban" : "unsuspend", { reason }))}>Unsuspend / Unban</ActionBtn>
                ) : (
                  <>
                    <ActionBtn tone="warn" testid="cc-action-suspend" onClick={() => run({ title: "Suspend member", description: "Blocks all authenticated access for the chosen period.", duration: true, cta: "Suspend" }, ({ reason, confirm, hours }) => act("suspend", { reason, confirm, hours }))}>Suspend</ActionBtn>
                    <ActionBtn tone="danger" testid="cc-action-ban" onClick={() => run({ title: "Ban member", description: "Permanent until unbanned. Sessions are revoked immediately.", highImpact: true, cta: "Ban" }, ({ reason, confirm }) => act("ban", { reason, confirm }))}>Ban</ActionBtn>
                  </>
                )}
                <ActionBtn tone="warn" testid="cc-action-force-logout" onClick={() => run({ title: "Force logout", description: "Revokes every JWT and OAuth session. Member must sign in again.", highImpact: true, cta: "Force logout" }, ({ reason, confirm }) => act("force-logout", { reason, confirm }))}>Force logout</ActionBtn>
                {rec.live_now && <ActionBtn tone="danger" testid="cc-action-force-end-live" onClick={() => run({ title: "Force-end their Live", highImpact: true, cta: "End Live" }, ({ reason, confirm }) => ccApi.liveAction(rec.live_now.id, "force-end", { reason, confirm }))}>Force-end Live</ActionBtn>}
                {canEvidence && <ActionBtn testid="cc-action-evidence-user" onClick={() => run({ title: "Capture evidence (profile screenshot)", cta: "Capture" }, ({ reason }) => captureAndUpload({ target: { type: "user", id: m.id, accountId: m.id }, reasonCode: reason.slice(0, 60), selectorHint: "main" }))}><Camera size={12} className="inline mr-1" />Capture evidence</ActionBtn>}
              </div>
              <div className="pt-2 space-y-1.5">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">Restrictions</div>
                {RESTRICTIONS.map(([k, label]) => {
                  const r = rec.member.active_restrictions?.[k];
                  return (
                    <div key={k} className="flex items-center justify-between gap-2 text-xs" data-testid={`cc-restriction-${k}`}>
                      <span className="text-zinc-300">{label} {r && <Pill tone="amber">restricted{r.until ? ` until ${fmtTs(r.until)}` : " (indefinite)"}</Pill>}</span>
                      {r ? (
                        <ActionBtn tone="good" testid={`cc-unrestrict-${k}`} onClick={() => run({ title: `Lift ${label} restriction`, cta: "Lift" }, ({ reason }) => act("unrestrict", { reason, kind: k }))}>Lift</ActionBtn>
                      ) : (
                        <ActionBtn tone="warn" testid={`cc-restrict-${k}`} onClick={() => run({ title: `Restrict ${label}`, duration: true, cta: "Restrict" }, ({ reason, hours }) => act("restrict", { reason, kind: k, hours }))}>Restrict</ActionBtn>
                      )}
                    </div>
                  );
                })}
              </div>
            </Section>
          )}
          <Section title={`Account history · ${rec.audit.length + rec.legacy_audit.length} entries`} testid="cc-member-history">
            {rec.cases.length > 0 && <div className="text-xs text-zinc-400">Cases: {rec.cases.map((c) => <Pill key={c.id} tone={c.status === "actioned" ? "amber" : "zinc"}>{c.category} · {c.status}</Pill>)}</div>}
            {rec.reports.length > 0 && <div className="text-xs text-zinc-400">{rec.reports.length} report(s) against · {rec.reports.filter((r) => r.status === "open").length} open</div>}
            {rec.audit.length === 0 && rec.legacy_audit.length === 0 && <Empty>No moderation history.</Empty>}
            {rec.audit.map((row) => <AuditRow key={row.id} row={row} />)}
            {rec.legacy_audit.map((row) => <AuditRow key={row.id} row={{ ...row, result: "success", actor_role: "admin (legacy)" }} />)}
          </Section>
        </>
      )}
      <ReasonDialog spec={dlg} onClose={() => setDlg(null)} onSubmit={submit} />
    </div>
  );
}
